import { memoize } from 'lodash';
import { i18nConfigService } from '@/workbench/pages/development/i18n/service';
import { transpileWithESBuild } from '@/components/esbuild';
import { fetchProjectInfo } from '@/store/project/project';
import store from '@/store';
import wujiFetch from '@/utils/wujiFetch';
import getXyManagePathPrefix from '@utils/getXyManagePathPrefix';
import { aiProjectEditorRoutePath } from '@/router/route-const';
import { BASE_PATH } from '@/config/constant';
import { useRouter } from '@/router/useRouter';
import { getLanguageFromFilename } from './language';
function getRouteParams() {
    const { route } = useRouter();
    const query = route.value?.query || {};
    return {
        projectId: String(query.projectid || ''),
        branch: String(query.branch || ''),
    };
}
export var I18nPreprocessType;
(function (I18nPreprocessType) {
    I18nPreprocessType["LessCodeModule"] = "lessCodeModule";
    I18nPreprocessType["AICode"] = "aiCode";
})(I18nPreprocessType || (I18nPreprocessType = {}));
const CACHE_TTL = 5 * 60 * 1000;
export const getI18nEnabled = memoize(async (projectId) => {
    setTimeout(() => getI18nEnabled.cache.delete(projectId), CACHE_TTL);
    try {
        const config = await i18nConfigService.get(projectId);
        return !!config?.enabled;
    }
    catch {
        return false;
    }
});
export const invalidateI18nCache = () => {
    getI18nEnabled.cache.clear();
};
export const preprocessI18nFiles = async ({ files, type, }) => {
    const { projectId } = getRouteParams();
    const enabled = await getI18nEnabled(projectId);
    if (!enabled)
        return { files, mappings: [] };
    const url = `${getXyManagePathPrefix()}/i18n/${projectId}/preprocess`;
    const result = await wujiFetch(url, {
        method: 'POST',
        body: JSON.stringify({ files, type }),
    });
    return {
        files: result?.files ?? files,
        mappings: result?.mappings ?? [],
    };
};
export const preprocessAndCompileModule = async (filename, source) => {
    const { files } = await preprocessI18nFiles({ files: { [filename]: source }, type: I18nPreprocessType.LessCodeModule });
    const processed = files[filename] || source;
    const out = await transpileWithESBuild(processed, filename);
    return out.code;
};
// 是否需要编译? 只有 ts 或者使用了可选链语法，或者使用了 esm 语法才需要编译
export const needsTranspile = (filename, source) => {
    const language = getLanguageFromFilename(filename);
    return language !== 'javascript' || /^(export|import)\s|\?\.[\w[]/m.test(source);
};
export const recompileAndSaveModules = async () => {
    const { projectId } = getRouteParams();
    const project = await fetchProjectInfo(projectId);
    const modules = project?.lessCodeModules;
    if (!modules)
        return;
    let changed = false;
    for (const mod of Object.values(modules)) {
        if (!mod.source)
            continue;
        if (needsTranspile(mod.filename, mod.source)) {
            const out = await transpileWithESBuild(mod.source, mod.filename);
            mod.code = out.code;
        }
        else {
            mod.code = mod.source;
        }
        changed = true;
    }
    if (changed) {
        await store.dispatch('updateProject', {
            projectId,
            projectInfo: { id: projectId, lessCodeModules: modules },
            isUpdateByDev: true,
        });
    }
};
export var AIEditorSaveState;
(function (AIEditorSaveState) {
    AIEditorSaveState["Ready"] = "ready";
    AIEditorSaveState["Finish"] = "finish";
})(AIEditorSaveState || (AIEditorSaveState = {}));
export const requestAIEditorSave = ({ timeout = 1200_000, onStateChange, } = {}) => {
    const { projectId, branch } = getRouteParams();
    return new Promise((resolve, reject) => {
        const requestId = Math.random().toString(36).slice(2);
        const params = new URLSearchParams({ projectid: projectId, loadInIframe: '1' });
        if (branch)
            params.set('branch', branch);
        const basePath = (BASE_PATH || '').replace(/\/$/, '');
        const container = document.createElement('div');
        container.className = 'w-0 h-0 pos-absolute overflow-hidden';
        const iframe = document.createElement('iframe');
        iframe.src = `${basePath}${aiProjectEditorRoutePath}?${params}`;
        container.appendChild(iframe);
        let settled = false;
        const timer = setTimeout(() => finish(false, 'AI editor save timed out', true), timeout);
        const handleMessage = (event) => {
            if (event.data?.source !== 'xy-msg')
                return;
            if (event.data.type === 'ready') {
                onStateChange?.(AIEditorSaveState.Ready, 50);
                iframe.contentWindow?.postMessage({
                    source: 'xy-msg',
                    type: 'save',
                    requestId,
                }, '*');
            }
            else if (event.data.type === 'response' && event.data.payload?.requestId === requestId) {
                onStateChange?.(AIEditorSaveState.Finish, 100);
                const success = event.data.payload?.success ?? true;
                finish(success, event.data.payload?.error);
            }
        };
        function finish(success, error, timedOut = false) {
            if (settled)
                return;
            settled = true;
            clearTimeout(timer);
            window.removeEventListener('message', handleMessage);
            container.remove();
            if (success) {
                resolve(true);
                return;
            }
            reject(new Error(timedOut ? 'AI editor save timed out' : (error || 'AI editor save failed')));
        }
        window.addEventListener('message', handleMessage);
        document.body.appendChild(container);
    });
};
export const preprocessCompileAndSaveModules = async () => {
    const { projectId } = getRouteParams();
    const project = await fetchProjectInfo(projectId);
    const modules = project?.lessCodeModules;
    if (!modules)
        return;
    const sourceMap = {};
    for (const mod of Object.values(modules)) {
        if (!mod.source && mod.code)
            mod.source = mod.code;
        if (mod.source)
            sourceMap[mod.filename] = mod.source;
    }
    if (!Object.keys(sourceMap).length)
        return;
    const { files: processed } = await preprocessI18nFiles({ files: sourceMap, type: I18nPreprocessType.LessCodeModule });
    for (const mod of Object.values(modules)) {
        if (!mod.source)
            continue;
        const src = processed[mod.filename] || mod.source;
        if (needsTranspile(mod.filename, src)) {
            const out = await transpileWithESBuild(src, mod.filename);
            mod.code = out.code;
        }
        else {
            mod.code = src;
        }
    }
    await store.dispatch('updateProject', {
        projectId,
        projectInfo: { id: projectId, lessCodeModules: modules },
        isUpdateByDev: true,
    });
};
